import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// 子菜单配置
const subMenus: Record<string, Array<{ label: string; key: string }>> = {
  '/installation': [
    { label: '安装概览', key: 'overview' },
    { label: '全局安装', key: 'manual' },
    { label: '从源代码', key: 'source' },
    { label: '其他选项', key: 'other' },
    { label: '维护', key: 'maintain' }
  ],
  '/channels': [
    { label: '通道概览', key: 'overview' },
    { label: 'WhatsApp', key: 'whatsapp' },
    { label: 'Telegram', key: 'telegram' },
    { label: 'Discord', key: 'discord' },
    { label: 'Slack', key: 'slack' },
    { label: 'Mattermost', key: 'mattermost' },
    { label: 'Signal', key: 'signal' },
    { label: 'iMessage', key: 'imessage' }
  ],
  '/skills': [
    { label: '安装', key: 'install' },
    { label: '技能下载', key: 'downloads' }
  ],
  '/agents': [
    { label: '概念概览', key: 'overview' },
    { label: '架构', key: 'architecture' },
    { label: '智能体', key: 'agent' },
    { label: '智能体循环', key: 'agent-loop' },
    { label: '工作区', key: 'workspace' },
    { label: '会话', key: 'session' },
    { label: '记忆', key: 'memory' },
    { label: '压缩', key: 'compaction' },
    { label: '消息', key: 'messages' },
    { label: '流式传输', key: 'streaming' },
    { label: '模型', key: 'models' },
    { label: 'OAuth认证', key: 'oauth' },
    { label: '模型故障转移', key: 'model-failover' },
    { label: '多智能体路由', key: 'multi-agent' }
  ],
  '/tools': [
    { label: '工具概览', key: 'overview' },
    { label: '浏览器', key: 'browser' },
    { label: '技能（Skills）', key: 'skills' },
    { label: '斜杠命令', key: 'slash-commands' },
    { label: '子智能体', key: 'subagents' }
  ],
  '/gateway': [
    { label: '网关概览', key: 'overview' },
    { label: '配置', key: 'config' },
    { label: '认证', key: 'auth' },
    { label: '安全', key: 'security' },
    { label: '远程访问', key: 'remote-access' },
    { label: '日志', key: 'logs' },
    { label: '沙箱', key: 'sandbox' },
    { label: '故障排除', key: 'troubleshooting' }
  ],
  '/cases': [
    { label: '全部案例', key: 'all' },
    { label: '社交媒体', key: 'social' },
    { label: '金融投资', key: 'finance' },
    { label: '办公自动化', key: 'office' },
    { label: '生态集成', key: 'ecosystem' },
    { label: '健康管理', key: 'health' },
    { label: '开发部署', key: 'development' }
  ],
  '/models': [
    { label: '提供商概览', key: 'overview' },
    { label: 'Anthropic', key: 'anthropic' },
    { label: 'OpenAI', key: 'openai' },
    { label: 'OpenRouter', key: 'openrouter' }
  ],
  '/platform': [
    { label: '平台概览', key: 'overview' },
    { label: 'macOS', key: 'macos' },
    { label: 'Linux', key: 'linux' },
    { label: 'Windows', key: 'windows' },
    { label: 'iOS', key: 'ios' },
    { label: 'Android', key: 'android' }
  ]
};

export default function Navbar() {
  const [showWhiteBg, setShowWhiteBg] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setIsLoaded(true);
    const handleScroll = () => {
      setShowWhiteBg(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: '首页', path: '/' },
    { label: '快速开始', path: '/quick-start' },
    { label: '安装', path: '/installation' },
    { label: '消息渠道', path: '/channels' },
    { label: '技能', path: '/skills' },
    { label: '工具', path: '/tools' },
    { label: '网关', path: '/gateway' },
    { label: '实战案例', path: '/cases' },
    { label: '参考', path: '/reference' },
    { label: '帮助', path: '/help' }
  ];

  const handleSubMenuClick = (path: string, key: string) => {
    const storageKeys: Record<string, string> = {
      '/installation': 'installation_active_section',
      '/channels': 'channels_active_section',
      '/skills': 'skills_active_section',
      '/agents': 'agents_active_section',
      '/tools': 'tools_active_section',
      '/gateway': 'gateway_active_section',
      '/models': 'models_active_section',
      '/platform': 'platform_active_section',
    };

    const storageKey = storageKeys[path];
    if (storageKey) {
      localStorage.setItem(storageKey, key);
      // 派发自定义事件，让同页面的监听器能响应
      window.dispatchEvent(new CustomEvent('nav-section-change', {
        detail: { storageKey, key }
      }));
    }

    setHoveredMenu(null);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      showWhiteBg ? 'bg-slate-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-6">
          <Link 
            to="/" 
            className={`flex items-center gap-3 transition-all duration-500 cursor-pointer flex-shrink-0 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
          >
            <img src="https://static.readdy.ai/image/335e3e62ea46a4ee8fffcd870552cca1/aabcd16da587369b7c8ac1f1b999503f.svg" alt="Openclaw" className="w-10 h-10" />
            <span className={`text-xl font-bold ${showWhiteBg ? 'text-white' : 'text-white'}`}>
              OpenClaw<span className="text-cyan-400">中文网</span>
            </span>
          </Link>
          
          <div className="hidden lg:flex items-center gap-1 flex-1 ml-16">
            {navItems.map((item, index) => {
              const hasSubMenu = subMenus[item.path];
              const isActive = location.pathname === item.path;
              
              return (
                <div 
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => hasSubMenu && setHoveredMenu(item.path)}
                  onMouseLeave={() => setHoveredMenu(null)}
                >
                  <Link 
                    to={item.path} 
                    className={`px-3 py-1.5 transition-all duration-500 cursor-pointer font-medium text-[15px] rounded-md hover:bg-slate-800/50 whitespace-nowrap tracking-wide ${
                      isActive
                        ? 'text-cyan-400 bg-slate-800/50' 
                        : showWhiteBg 
                          ? 'text-gray-300 hover:text-cyan-400' 
                          : 'text-white/90 hover:text-cyan-400'
                    } ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
                    style={{ transitionDelay: `${(index + 1) * 50}ms` }}
                  >
                    {item.label}
                  </Link>
                  
                  {/* 下拉子菜单 */}
                  {hasSubMenu && hoveredMenu === item.path && (
                    <div className="absolute top-full left-0 mt-1 bg-slate-900/98 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 py-2 min-w-[200px] z-50">
                      {subMenus[item.path].map((subItem) => (
                        <Link
                          key={subItem.key}
                          to={item.path}
                          onClick={() => handleSubMenuClick(item.path, subItem.key)}
                          className="block px-4 py-2 text-[14px] text-gray-300 hover:text-cyan-400 hover:bg-slate-800/50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 移动端菜单按钮 */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 cursor-pointer ml-auto"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-slate-900/98 backdrop-blur-sm border-t border-slate-700/50">
          <div className="px-4 py-2 space-y-1">
            {navItems.map((item) => {
              const hasSubMenu = subMenus[item.path];
              const isActive = location.pathname === item.path;
              
              return (
                <div key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => !hasSubMenu && setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 rounded-lg text-sm font-medium cursor-pointer ${
                      isActive
                        ? 'text-cyan-400 bg-slate-800/50'
                        : 'text-gray-300 hover:text-cyan-400 hover:bg-slate-800/50'
                    }`}
                  >
                    {item.label}
                  </Link>
                  
                  {/* 移动端子菜单 */}
                  {hasSubMenu && isActive && (
                    <div className="ml-4 mt-1 space-y-1">
                      {subMenus[item.path].map((subItem) => (
                        <Link
                          key={subItem.key}
                          to={item.path}
                          onClick={() => handleSubMenuClick(item.path, subItem.key)}
                          className="block px-3 py-2 text-sm text-gray-400 hover:text-cyan-400 hover:bg-slate-800/30 rounded-lg cursor-pointer"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}
